/**
 * **The Technical Delegate's Match Debrief** — `/fixtures/:tmsMatchDbId/debrief` (spec 13 §9,
 * run 75). A WINDOW, not a workbench.
 *
 * Authority: `um-app/13-match-debrief.md` §3, §5 and §9; `um-app/11-release-and-debrief.md` §3
 * (*"the TD sees released notes at the same moment the panel does — no delay, no TD-blind
 * tier"*); `um-app/00-overview.md` §2 (the TD authors his own stream, the panel authors theirs);
 * `control-centre/02-fixtures.md` (the module this screen hangs off).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS SCREEN IS FOR
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The Umpires Managers hold a post-match debrief with the match's Umpire 1, Umpire 2 and Video
 * Umpire, and `pages/um/UmMatchDebriefPage` is where they hold it. Everything that comes out of
 * that meeting — the organised summary of what the panel released about each umpire, the released
 * notes it was built from, and what the umpire said back — has, until now, had no surface in the
 * Control Centre at all. The Technical Delegate could read the raw released notes at
 * `GET /notes/released` and nothing assembled them for him.
 *
 * This is that assembly, per match, and it is **exactly the UM screen's content minus every
 * control**. It adds no figure of its own: no author-level filter, no sort, no cross-umpire
 * aggregate, no count the UM screen does not also carry. If a number is not on the panel's own
 * screen it is not on this one, because a supervision surface that quietly computed more than
 * the people being supervised can see would be a different thing wearing the same name.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * READ ONLY, AND WHERE THAT IS ENFORCED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **On this page**: the composer, `Release Responses`, `Lock`, `Undo Release` and the unlock
 * confirmation are ABSENT — not disabled, not hidden behind a flag, not imported. This module
 * holds no reference to `captureNote`, `lockNote`, `releaseMatchNotes` or `unreleaseNote`, so
 * there is nothing here for a later change to re-enable by removing a `disabled` attribute. The
 * shared card components it draws (`features/debrief/DebriefSeat.tsx`) carry no write prop at
 * all, for the same reason.
 *
 * **On the server**: the TD path onto this content is three reads and no verb —
 * `GET /td/matches/:matchId/umpires` (run 75, `requireTd`), `GET /matches/:matchId/umpires/
 * :tmsPersonId/debrief-summary` and `GET /notes/released`. The UM app's write routes are all
 * `requireUm` and refuse a TD credential with a 403, which is the property the API tests hold.
 *
 * The kicker says so in the reader's own words: **`Read Only · The Panel Writes Here`**. It is
 * not an apology for a missing feature. A debrief response is one manager's record of what an
 * umpire said to her, and the Technical Delegate writing into that stream would put words in her
 * mouth on a record the panel is accountable for.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ROOM-SAFE CONTENT SET, PLUS THE ALIGNMENT SENTENCE (RUN 80)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The UM screen has two views (ruling U1). Room View is the default there because a phone on a
 * table is read from both sides of it; My View adds the author chips (`UM · Naoko`), the note
 * counts, and the `worth aligning` closing sentence under the kicker `For The Managers`.
 *
 * **This screen has one view, with no toggle.** It draws the room-safe set — no author chip and
 * no note-count pill — **and, since run 80, the `For The Managers` sentence**: the Technical Delegate
 * decided on 13 September 2026 that his screen shows it. The sentence names no manager and counts
 * nothing; it says where the panel's released readings of an umpire differ, which is a question
 * about the very debrief he is reading.
 *
 * What keeps the rest off is who it belongs to, not privacy. Which manager wrote which note, and
 * how many notes stand behind each umpire, are the PANEL's own working facts, and this screen
 * declines to re-present them as though they were part of the debrief record. Nothing is hidden
 * from him: every released note is his to list at `GET /notes/released`, with its author on it,
 * and ruling R4″ gives him each manager's recorded, pending and released counts on his own UM
 * Activity panel (`GET /td/um-activity`). *(Corrected in run 80: this paragraph used to say that
 * R4″ had put a manager's unreleased-note count out of the Technical Delegate's reach. R4″ did
 * the opposite.)*
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DATE AND MATCH, AND NOTHING ELSE TO CHOOSE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Two native selects — matchday, then match — the UM screen's own idiom and the same labels
 * (`matchdayLabel`). They are the whole of the selection this screen offers. A competition filter,
 * an umpire filter or an author filter would each be a way of asking a question about the panel
 * rather than about a match, and §9's line is that the TD's view is *per match*.
 *
 * A read that fails is said plainly and never dressed as an empty debrief: an unreachable summary
 * renders that umpire's notice with the released notes still under it, exactly as on the phone.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  fetchFixtureDays,
  fetchMatchDebriefSummary,
  fetchReleasedNotes,
  fetchTdMatchUmpires,
} from "../api/client.js";
import type { AppointedUmpireBody, FixtureRowView, ReleasedNoteView } from "../api/types.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { usePageKicker } from "../app/pageKicker.js";
import { FailBanner, Skeleton } from "../app/states.js";
import { useAsync } from "../app/useAsync.js";
import {
  DebriefSeatHeader,
  DebriefSeatObservations,
  DebriefSeatSummary,
  type SummaryEntry,
} from "../features/debrief/DebriefSeat.js";
import {
  debriefSeats,
  matchdayLabel,
  RESPONSES_HEADING,
} from "../pages/um/matchDebrief.js";

/**
 * The page's own kicker, **verbatim**, and it is asserted character for character by the test.
 *
 * It is the one sentence on this screen that is about the screen rather than about the match, and
 * it is two statements in one: what the reader may do here, and who the author is. `The Panel
 * Writes Here` is the half that matters — the absence of a composer is not a gap in the build.
 */
export const TD_DEBRIEF_KICKER = "Read Only · The Panel Writes Here";

/** The masthead line, which names the screen; the page's own kicker carries the ruling. */
const TD_DEBRIEF_MASTHEAD_KICKER = "Match Debrief · What The Panel Recorded · Read Only";

/**
 * `/fixtures/:tmsMatchDbId/debrief` — the route, and the one place it is spelled.
 *
 * **It hangs off the Fixtures module** (`control-centre/02-fixtures.md`, module 03) rather than
 * claiming a strip entry of its own, because the thing it is about is one fixture and the
 * Control Centre already has a screen listing every fixture there is. `moduleForPath` therefore
 * lights `Fixtures` in the nav and names it in the breadcrumb, which is where a Technical
 * Delegate who followed a match row to get here would expect to be.
 *
 * **The key is the TMS DATABASE match id, never the match number** — gate G-12, and the reason
 * `UmCapturePage` gives at length: `M07` is not unique across the two competitions and a knockout
 * renumbering would silently point the screen at another match.
 *
 * **No competition travels in the query**, and that absence is the read-only rule showing through
 * the URL. The UM screen carries one because a debrief response is a note and a note must carry a
 * competition (`note.competition_id` is NOT NULL); this screen writes nothing, so it needs none.
 */
export function tdMatchDebriefPath(tmsMatchDbId: number): string {
  return `/fixtures/${String(tmsMatchDbId)}/debrief`;
}

/** How often the released set is re-read, in step with the phone's own poll. */
const POLL_MS = 60_000;

export function TdMatchDebriefPage(): React.JSX.Element {
  const { tmsMatchDbId } = useParams<{ tmsMatchDbId: string }>();
  const navigate = useNavigate();
  usePageKicker(TD_DEBRIEF_MASTHEAD_KICKER);

  const matchId = Number.parseInt(tmsMatchDbId ?? "", 10);

  /** Bumped by the poll below, which is the only thing on this screen that changes anything. */
  const [readKey, setReadKey] = useState(0);

  const schedule = useAsync(() => fetchFixtureDays(), []);
  const appointed = useAsync(
    () => (Number.isInteger(matchId) ? fetchTdMatchUmpires(matchId) : Promise.resolve(null)),
    [matchId],
  );
  const released = useAsync(
    () =>
      Number.isInteger(matchId)
        ? fetchReleasedNotes({ match: matchId })
        : Promise.resolve(null),
    [matchId, readKey],
  );

  const fixtures: readonly FixtureRowView[] =
    schedule.status === "ready" ? schedule.data.fixtures : [];
  const fixture = fixtures.find((row) => row.tmsMatchDbId === matchId) ?? null;

  const appointments: readonly AppointedUmpireBody[] =
    appointed.status === "ready" && appointed.data !== null ? appointed.data.umpires : [];
  const seats = debriefSeats(appointments);
  const seatKey = seats.map((seat) => seat.tmsPersonId).join(",");

  /**
   * One Call 6 read per umpire, in parallel, and **each one's failure is its own** — the phone's
   * own arrangement, for the phone's own reason: a screen that went blank because one summary
   * could not be built would have thrown away two summaries and every released note on it.
   */
  const summaries = useAsync(
    () =>
      seats.length === 0
        ? Promise.resolve<SummaryEntry[]>([])
        : Promise.all(
            seats.map(
              (seat): Promise<SummaryEntry> =>
                fetchMatchDebriefSummary(matchId, seat.tmsPersonId).then(
                  (body): SummaryEntry => ({ tmsPersonId: seat.tmsPersonId, ok: true, body }),
                  (): SummaryEntry => ({ tmsPersonId: seat.tmsPersonId, ok: false }),
                ),
            ),
          ),
    [matchId, seatKey, readKey],
  );

  /**
   * The quiet refresh.
   *
   * Unlike the phone's, this one simply re-reads: nothing here is being read aloud to an umpire,
   * so there is no paragraph to protect from being rewritten mid-sentence, and a Technical
   * Delegate looking at a match ten minutes after the debrief wants the release that has just
   * landed. It is still only a READ — the poll cannot change a note, only what this screen has
   * been told about one.
   */
  useEffect(() => {
    if (!Number.isInteger(matchId)) return undefined;
    const timer = setInterval(() => {
      setReadKey((count) => count + 1);
    }, POLL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [matchId]);

  const shownReleased: readonly ReleasedNoteView[] =
    released.status === "ready" && released.data !== null ? released.data.notes : [];
  const shownSummaries: readonly SummaryEntry[] =
    summaries.status === "ready" ? summaries.data : [];

  const days = [...new Set(fixtures.map((row) => row.date))].sort();
  const day = fixture?.date ?? null;
  const dayFixtures = fixtures.filter((row) => row.date === day);

  const go = (row: FixtureRowView): void => {
    navigate(tdMatchDebriefPath(row.tmsMatchDbId));
  };

  if (!Number.isInteger(matchId)) {
    return (
      <section className="panel panel--d1">
        <div className="panel__head">
          <h3 className="panel__title">Match Debrief</h3>
        </div>
        <div className="emptystate">
          <b>This Link Does Not Name A Match</b>
          Open a match from the Fixtures list and the debrief for it opens with it.
        </div>
      </section>
    );
  }

  const matchNo = fixture?.displayMatchNo ?? null;
  const competitionName =
    fixture === null ? null : fixture.competitionGender === "m" ? "Men’s Competition" : "Women’s Competition";

  return (
    <div className="tdmd">
      <header className="tdmd__head">
        <nav className="tdmd__back" aria-label="Back to fixtures">
          <Link to="/fixtures">‹ Fixtures</Link>
        </nav>
        <h2 className="tdmd__title">
          Match Debrief
          {matchNo === null ? null : <span className="tdmd__mno num">{matchNo}</span>}
        </h2>
        <span className="tdmd__sub">
          {[competitionName, fixture === null ? null : `${fixture.time} · ${fixture.pitch}`]
            .filter((part): part is string => part !== null)
            .join(" · ")}
        </span>
        {/*
         * REQUIREMENT 5's kicker, verbatim. It is drawn on the page and not only claimed for the
         * masthead, because the sentence is about what the reader may do on THIS screen and a
         * line that only exists above the module strip is a line a reader scrolls past once.
         */}
        <span className="kicker kicker--accent tdmd__kicker" data-testid="tdmd-kicker">
          {TD_DEBRIEF_KICKER}
        </span>

        <div className="tdmd__pickers">
          {days.length > 0 && day !== null ? (
            <label className="dbday" data-howto="tdmd-matchday">
              <span className="dbday__lbl">Matchday</span>
              <select
                className="dbday__sel"
                value={day}
                data-testid="tdmd-matchday-select"
                onChange={(event) => {
                  const first = fixtures.find((row) => row.date === event.target.value);
                  if (first !== undefined) go(first);
                }}
              >
                {days.map((entry) => (
                  <option key={entry} value={entry}>
                    {matchdayLabel(days, entry)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {dayFixtures.length > 0 ? (
            <label className="dbday" data-howto="tdmd-match">
              <span className="dbday__lbl">Match</span>
              <select
                className="dbday__sel"
                value={String(matchId)}
                data-testid="tdmd-match-select"
                onChange={(event) => {
                  const row = fixtures.find(
                    (entry) => String(entry.tmsMatchDbId) === event.target.value,
                  );
                  if (row !== undefined) go(row);
                }}
              >
                {dayFixtures.map((row) => (
                  <option key={row.tmsMatchDbId} value={String(row.tmsMatchDbId)}>
                    {`${row.displayMatchNo} · ${row.time}`}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        <HowToUse id="td-match-debrief" title="How to use the Match Debrief">
          <li>
            This is the Umpires Managers&rsquo; post-match debrief with <b>Umpire 1</b>,{" "}
            <b>Umpire 2</b> and the <b>Video Umpire</b>, as they recorded it. The reserve umpire is
            not part of it.
          </li>
          <li>
            <b>You cannot write here.</b> The summary, the notes and the umpire&rsquo;s own words
            are the panel&rsquo;s; your own notes are captured on the Command Desk and kept in a
            separate stream.
          </li>
          <li>
            Each umpire&rsquo;s block is the released notes about them, organised. Open{" "}
            <b>Show Notes</b> to read the notes it was built from — they are the evidence,
            whatever the summary says.
          </li>
          <li>
            <b>For The Managers</b>, when it appears under a summary, is where the managers&rsquo;
            released notes read the same thing differently. It names nobody.
          </li>
          <li>
            <b>{RESPONSES_HEADING}</b> is what the umpire said back, once a manager has released
            it. An empty section means nothing has been released yet, not that nothing was said.
          </li>
          <li>
            <HowToLink to="tdmd-matchday">The matchday and match pickers</HowToLink> reach any
            match of the tournament.
          </li>
        </HowToUse>
      </header>

      {schedule.status === "error" ? <FailBanner error={schedule.error} /> : null}
      {appointed.status === "error" ? <FailBanner error={appointed.error} /> : null}
      {released.status === "error" ? <FailBanner error={released.error} /> : null}

      {(released.status === "loading" || summaries.status === "loading") &&
      shownSummaries.length === 0 ? (
        <Skeleton rows={5} />
      ) : null}

      {appointed.status === "ready" && seats.length === 0 ? (
        <section className="card mdcard">
          <div className="card__head">
            <span className="card__kicker">Nobody Is Appointed To This Match</span>
          </div>
          <p className="card__body">
            The umpires managers&rsquo; appointments for this match are not held in this system, so
            there is no panel to draw. Nothing has been lost: every released note about this match
            is on the umpire&rsquo;s own profile.
          </p>
        </section>
      ) : null}

      {seats.map((seat) => {
        const entry = shownSummaries.find((row) => row.tmsPersonId === seat.tmsPersonId) ?? null;
        const observations = shownReleased.filter(
          (note) => note.umpireId === seat.tmsPersonId && note.kind === "observation",
        );
        const responses = shownReleased.filter(
          (note) => note.umpireId === seat.tmsPersonId && note.kind === "debrief_response",
        );

        return (
          <section
            className="card mdcard"
            key={seat.tmsPersonId}
            data-testid={`tdmd-card-${seat.tmsPersonId}`}
          >
            <DebriefSeatHeader seat={seat} />

            {/* `roomView` is held true on this screen and there is no toggle; `showAlignment`
                draws the `For The Managers` sentence all the same (run 80) — see the header. */}
            <DebriefSeatSummary seat={seat} entry={entry} roomView showAlignment />

            <details className="mddisc">
              <summary className="mddisc__sum">
                <span className="card__kicker">Show Notes ▾</span>
              </summary>
              <div className="mddisc__body">
                <DebriefSeatObservations observations={observations} roomView />
              </div>
            </details>

            {/*
             * WHAT THE UMPIRE SAID — open, because it is the half of the debrief that exists
             * nowhere else in the Control Centre. **Released responses only.** A manager's draft
             * is hers until she releases it, and there is no `GET /notes/mine` on this page to
             * reach one with.
             */}
            <div className="mdsaid">
              <div className="notelist__head">
                <span className="card__kicker">{RESPONSES_HEADING}</span>
              </div>
              {responses.length === 0 ? (
                <p className="card__body">Nothing has been released yet.</p>
              ) : (
                <ul className="mdnotes">
                  {responses.map((note) => (
                    <li className="mdnote" key={note.id} data-testid={`tdmd-response-${note.id}`}>
                      <span className="mdnote__txt">{note.bodyEn}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
